import {
  combineComputedAggregates,
  IMetricAggregateInput,
  MetricAggregate,
} from '@hawkindynamics/core-client';
import {
  collection,
  doc,
  DocumentData,
  DocumentSnapshot,
  Firestore,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import { collectionData, doc as fsDoc } from 'rxfire/firestore';
import { combineLatest, EMPTY, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { v4 } from 'uuid';
import { convertAggregateToImperial } from '../../utils/conversions';
import { startOfToday } from '../../utils/date';
import { validAggregate } from '../../utils/filters';
import {
  EntityType,
  IAggregate,
  ICombinedComputedAggregate,
  IComputedAggregate,
  IComputedMetricAggregateMap,
  IRange,
  IUser,
} from '../types';
import { IMetrics, metrics } from './metricsProvider';
import {
  comparisonPopulationSettingsProvider,
  IComparisonPopulationSetting,
} from './settings/comparisonPopulationSettingsProvider';
import {
  convertDateOptionValue,
  IExclusiveDateOption,
  IInclusiveDateOption,
} from './settings/dates/dateRangeProvider';
import {
  IMetricSettings,
  metricSettings,
} from './settings/metricSettingsProvider';
import { authenticatedUserProvider } from './userProvider';
import { getSessionRangeTimestamps } from './utils/getSessionRangeTimestamps';

export interface IComparisonAggregates extends IAggregates {
  settings: IComparisonPopulationSetting;
}

export interface IAggregates {
  sessions: IComputedAggregate[];
  combined: IComputedAggregate;
  eventId: string;
  start: number;
  end: number;
}

/**
 * @description observes the aggregates for a comparison population (computedAggregates and sessions) for the supplied
 *	current settings and query hash
 * @param queryHash
 */
export const comparisonPopulationAggregatesProvider = (
  self: string,
  queryHash: string,
  afterSessionId: string
): Observable<IComparisonAggregates> => {
  return comparisonPopulationSettingsProvider().pipe(
    switchMap(
      (
        settings: IComparisonPopulationSetting
      ): Observable<IComparisonAggregates> => {
        /// convert date range in database to actual values for query
        /// determine the entityType and entityId to use in the queries
        const isSelf = settings.entity.value === 'self';
        let parsedEntity = parseEntityString(self);
        if (!isSelf) {
          parsedEntity = parseEntityString(settings.entity.value);
        }
        /// observe the aggregate documents
        return observeAggregates(
          parsedEntity.entityId,
          parsedEntity.entityType,
          queryHash,
          settings.dateRangeType,
          settings.dateRangeValues,
          afterSessionId
        ).pipe(
          map(
            (agg: IAggregates): IComparisonAggregates => ({ ...agg, settings })
          )
        );
      }
    )
  );
};

/**
 * @description observes the aggregates for the provided entity and date range
 * @param entityId
 * @param entityType
 * @param queryHash
 * @param dateType
 * @param dateRange
 * @param afterSessionId
 */
export const aggregatesProvider = (
  entityId: string,
  entityType: EntityType,
  queryHash: string,
  dateType: IInclusiveDateOption | IExclusiveDateOption,
  dateRange: IRange,
  afterSessionId?: string
): Observable<IAggregates> => {
  return observeAggregates(
    entityId,
    entityType,
    queryHash,
    dateType,
    dateRange,
    afterSessionId
  );
};

/**
 * @description observes the aggregate documents
 * @param entityId
 * @param entityType
 * @param queryHash
 * @param dateType
 * @param customDateRange
 * @param afterSesssionId
 */
const observeAggregates = (
  entityId: string,
  entityType: EntityType,
  queryHash: string,
  dateType: IExclusiveDateOption | IInclusiveDateOption,
  customDateRange: IRange,
  afterSessionId?: string
): Observable<IAggregates> => {
  return sessions({
    entityId,
    entityType,
    queryHash,
    dateType,
    customDateRange,
    afterSessionId,
  }).pipe(
    map((aggs: IComputedAggregate[]): IComputedAggregate[] =>
      aggs.filter(validAggregate)
    ),
    map((validSessions: IComputedAggregate[]): IAggregates => {
      const { start, end } = getSessionRangeTimestamps(validSessions);
      /// add unique id for this event emission for downstream consumers to use
      const eventId = v4();
      return {
        sessions: validSessions,
        combined: {
          meta: validSessions?.[0]?.meta,
          data: aggregateSessions(validSessions),
        },
        eventId,
        start,
        end,
      };
    }),
    switchMap((allAggregates: IAggregates): Observable<IAggregates> => {
      /// merge the results w/ active metrics
      return combineLatest([
        mergeMetrics(allAggregates.sessions),
        mergeMetrics([allAggregates.combined]),
      ]).pipe(
        map(
          (
            result: [IComputedAggregate[], IComputedAggregate[]]
          ): IAggregates => {
            const [sessions, combined] = result;
            return {
              sessions,
              combined: combined[0],
              eventId: allAggregates.eventId,
              start: allAggregates.start,
              end: allAggregates.end,
            };
          }
        )
      );
    })
  );
};

/**
 * @description listens to n sessions before and after the supplied sessionId
 * @param entityId
 * @param entityType
 * @param queryHash
 * @param window
 * @param sessionId
 * @returns
 */
export const aggregateWindowProvider = (
  entityId: string,
  entityType: EntityType,
  queryHash: string,
  window: 5 | 10 | 20,
  sessionId?: number
): Observable<IAggregates> => {
  return authenticatedUserProvider().pipe(
    switchMap((user: IUser): Observable<IAggregates> => {
      return windowQuery({
        orgId: user.orgId,
        entityId,
        entityType,
        queryHash,
        window,
        sessionId,
        firestore: user.services.firestore,
      }).pipe(
        map((aggs: IComputedAggregate[]): IComputedAggregate[] =>
          aggs.filter(validAggregate)
        ),
        map((validSessions: IComputedAggregate[]): IAggregates => {
          const { start, end } = getSessionRangeTimestamps(validSessions);
          /// add unique id for this event emission for downstream consumers to use
          const eventId = v4();
          return {
            sessions: validSessions,
            combined: {
              meta: validSessions?.[0]?.meta,
              data: aggregateSessions(validSessions),
            },
            eventId,
            start,
            end,
          };
        }),
        switchMap((allAggregates: IAggregates): Observable<IAggregates> => {
          /// merge the results w/ active metrics
          return combineLatest([
            mergeMetrics(allAggregates.sessions),
            mergeMetrics([allAggregates.combined]),
          ]).pipe(
            map(
              (
                result: [IComputedAggregate[], IComputedAggregate[]]
              ): IAggregates => {
                const [sessions, combined] = result;
                return {
                  sessions,
                  combined: combined[0],
                  eventId: allAggregates.eventId,
                  start: allAggregates.start,
                  end: allAggregates.end,
                };
              }
            )
          );
        })
      );
    })
  );
};

/**
 * @description parses an entity string into type and id
 * @param entityString
 */
const parseEntityString = (
  entityString: string
): { entityType: EntityType; entityId: string } => {
  const [entityType, entityId] = entityString.split(':');
  return {
    entityType: entityType as EntityType,
    entityId,
  };
};

const databaseToEntity = (
  // eslint-disable-next-line no-shadow
  database: Record<string, unknown>
): IComputedAggregate => {
  const metaKeys = ['count', 'id', 'testType', 'tags'];
  const entity: IComputedAggregate = { meta: null, data: null };
  for (const key in database) {
    if (metaKeys.includes(key)) {
      entity.meta = {
        ...entity.meta,
        [key]: database[key],
      };
    } else {
      entity.data = database.data as { [metricId: string]: IAggregate };
    }
  }
  return entity;
};

/**
 * @description merges the active metrics with the computed aggregate documents
 * @param data
 */
export const mergeMetrics = (
  data: IComputedAggregate[]
): Observable<IComputedAggregate[]> => {
  const knownComputedAggregates = data.filter(
    (mComputedAggregates: IComputedAggregate): boolean =>
      Boolean(mComputedAggregates?.meta?.testType)
  );
  return combineLatest([metrics, metricSettings]).pipe(
    map((result: [IMetrics, IMetricSettings]): IComputedAggregate[] => {
      const [allMetrics, settings] = result;
      return knownComputedAggregates.map(
        (computedAggregate: IComputedAggregate): IComputedAggregate => {
          const meta = allMetrics[computedAggregate.meta.testType];
          const mData: IComputedAggregate['data'] = {};
          const aggregateData = computedAggregate.data;

          for (const key in aggregateData) {
            if (aggregateData.hasOwnProperty(key)) {
              const metricMeta = meta[key];
              if (
                typeof aggregateData[key] === 'object' &&
                typeof aggregateData[key] !== 'string' &&
                !Array.isArray(aggregateData[key])
              ) {
                const aggregate: IAggregate = aggregateData[key] as IAggregate;

                // convert to imperial if imperial is requested and metric is measured in meters
                const convertedAggregate =
                  settings.imperial && metricMeta && metricMeta.units === 'm'
                    ? convertAggregateToImperial(aggregate)
                    : aggregate;

                mData[key] = {
                  ...metricMeta,
                  ...convertedAggregate,
                };
              } else {
                mData[key] = aggregateData[key];
              }
            }
          }

          return { meta: computedAggregate.meta, data: mData };
        }
      );
    })
  );
};

interface IAggregatesQuery {
  entityId: string;
  entityType: string;
  queryHash: string;
  dateType: IExclusiveDateOption | IInclusiveDateOption;
  customDateRange?: IRange;
  afterSessionId?: string;
}
/**
 * @description observes the sessions collection for the given entity, queryHash, and
 * date range
 * @param entityId
 * @param entityType
 * @param queryHash
 * @param range
 */
export const sessions = ({
  entityId,
  entityType,
  queryHash,
  dateType,
  customDateRange,
  afterSessionId,
}: IAggregatesQuery): Observable<IComputedAggregate[]> => {
  return authenticatedUserProvider().pipe(
    switchMap((user: IUser): Observable<IComputedAggregate[]> => {
      if (isEmpty(user)) return EMPTY;

      switch (dateType.value) {
        case 'all-time':
          return allTimeQuery({
            orgId: user.orgId,
            entityType,
            entityId,
            queryHash,
            firestore: user.services.firestore,
          });
        case 'custom':
          return rangeQuery({
            orgId: user.orgId,
            entityType,
            entityId,
            queryHash,
            range: customDateRange,
            firestore: user.services.firestore,
          });
        case 'two-date':
          return twoDateQuery({
            orgId: user.orgId,
            entityType,
            entityId,
            queryHash,
            range: customDateRange,
            firestore: user.services.firestore,
          });
        default:
          const numberOfSessions = convertDateOptionValue(dateType);
          return limitQuery({
            orgId: user.orgId,
            entityType,
            entityId,
            queryHash,
            numberOfSessions,
            afterSessionId,
            firestore: user.services.firestore,
          });
      }
    })
  );
};

interface ILimitQueryArgs {
  firestore: Firestore;
  orgId: string;
  entityType: string;
  entityId: string;
  queryHash: string;
  numberOfSessions: number;
  afterSessionId?: string;
}
/**
 * @description perform the query with a limit
 * @param args
 */
const limitQuery = (
  args: ILimitQueryArgs
): Observable<IComputedAggregate[]> => {
  const {
    orgId,
    entityId,
    entityType,
    queryHash,
    numberOfSessions,
    afterSessionId,
    firestore,
  } = args;
  if (afterSessionId) {
    return collectionData(
      query(
        collection(
          firestore,
          `/orgs/${orgId}/${entityType}/${entityId}/aggregates/${queryHash}/sessions`
        ),
        orderBy('id', 'desc'),
        startAfter(afterSessionId),
        limit(numberOfSessions)
      )
    ).pipe(
      map((sessions: Array<Record<string, unknown>>): IComputedAggregate[] =>
        sessions.map(databaseToEntity)
      )
    );
  } else {
    return collectionData(
      query(
        collection(
          firestore,
          `/orgs/${orgId}/${entityType}/${entityId}/aggregates/${queryHash}/sessions`
        ),
        orderBy('id', 'desc'),
        limit(numberOfSessions)
      )
    ).pipe(
      map((sessions: Array<Record<string, unknown>>): IComputedAggregate[] => {
        return sessions.map(databaseToEntity);
      })
    );
  }
};

export interface IRangeQueryArgs {
  firestore?: Firestore;
  orgId?: string;
  entityType?: string;
  entityId: string;
  queryHash: string;
  range: IRange;
}

/**
 * @description performs a query with a date range
 * @param args
 */
export const rangeQuery = (
  args: IRangeQueryArgs
): Observable<IComputedAggregate[]> => {
  const { orgId, entityId, entityType, queryHash, range, firestore } = args;
  return collectionData(
    query(
      collection(
        firestore,
        `/orgs/${orgId}/${entityType}/${entityId}/aggregates/${queryHash}/sessions`
      ),
      where('id', '>=', range.from.value.toString()),
      where(
        'id',
        '<=',
        range.to ? range.to.value.toString() : startOfToday().toString()
      ),
      orderBy('id', 'desc')
    )
  ).pipe(
    map((sessions: Array<Record<string, unknown>>): IComputedAggregate[] => {
      return sessions.map(databaseToEntity);
    })
  );
};

/**
 * @description performs a query between two specific dates
 * @param args
 */
const twoDateQuery = (
  args: IRangeQueryArgs
): Observable<IComputedAggregate[]> => {
  const { orgId, entityId, entityType, queryHash, range, firestore } = args;

  const from = collectionData(
    query(
      collection(
        firestore,
        `/orgs/${orgId}/${entityType}/${entityId}/aggregates/${queryHash}/sessions`
      ),
      where('id', '>=', range.from.value.toString()),
      where('id', '<', range.to.value.toString()),
      orderBy('id', 'asc'),
      limit(1)
    )
  );

  const to = collectionData(
    query(
      collection(
        firestore,
        `/orgs/${orgId}/${entityType}/${entityId}/aggregates/${queryHash}/sessions`
      ),
      where('id', '>', range.from.value.toString()),
      where('id', '<=', range.to.value.toString()),
      orderBy('id', 'desc'),
      limit(1)
    )
  );

  return combineLatest([from, to]).pipe(
    map((sessions: DocumentData[]): IComputedAggregate[] => {
      const returnVal = flatten(sessions).map(databaseToEntity);
      if (returnVal[0]?.meta.id === returnVal[1]?.meta.id) {
        return [];
      }
      return returnVal;
    })
  );
};

interface IAllTimeQueryArgs {
  orgId: string;
  entityType: string;
  entityId: string;
  queryHash: string;
  firestore: Firestore;
}

const allTimeQuery = (
  args: IAllTimeQueryArgs
): Observable<IComputedAggregate[]> => {
  const { orgId, entityId, entityType, queryHash, firestore } = args;
  return collectionData(
    collection(
      firestore,
      `/orgs/${orgId}/${entityType}/${entityId}/aggregates/${queryHash}/sessions`
    )
  ).pipe(
    map((docs: Array<Record<string, unknown>>): IComputedAggregate[] =>
      docs.map(
        (mDoc: Record<string, unknown>): IComputedAggregate =>
          databaseToEntity(mDoc)
      )
    )
  );
};

interface IWindowQueryArgs {
  orgId: string;
  entityId: string;
  entityType: EntityType;
  queryHash: string;
  window: 5 | 10 | 20;
  sessionId?: number;
  firestore: Firestore;
}

/**
 * @description queries for n sessions before and after the provided session id
 * @param args
 * @returns
 */
const windowQuery = (
  args: IWindowQueryArgs
): Observable<IComputedAggregate[]> => {
  const {
    orgId,
    entityId,
    entityType,
    queryHash,
    sessionId,
    window,
    firestore,
  } = args;
  return fsDoc(
    doc(
      firestore,
      `/orgs/${orgId}/${entityType}/${entityId}/aggregates/${queryHash}/sessions/${sessionId}`
    )
  ).pipe(
    switchMap(
      (docSnapshot: DocumentSnapshot): Observable<IComputedAggregate[]> => {
        const beforeQuery = collectionData(
          query(
            collection(
              firestore,
              `/orgs/${orgId}/${entityType}/${entityId}/aggregates/${queryHash}/sessions`
            ),
            orderBy('id', 'desc'),
            startAfter(docSnapshot),
            limit(window)
          )
        );

        const afterQuery = collectionData(
          query(
            collection(
              firestore,
              `/orgs/${orgId}/${entityType}/${entityId}/aggregates/${queryHash}/sessions`
            ),

            orderBy('id', 'asc'),
            startAfter(docSnapshot),
            limit(window)
          )
        );

        return combineLatest([beforeQuery, afterQuery]).pipe(
          map(
            (
              queryResults: [
                Array<Record<string, unknown>>,
                Array<Record<string, unknown>>
              ]
            ): IComputedAggregate[] => {
              return flatten([...queryResults, docSnapshot.data()]).map(
                (result: Record<string, unknown>): IComputedAggregate =>
                  databaseToEntity(result)
              );
            }
          )
        );
      }
    )
  );
};

/**
 * @description combines sessions into a single aggregate
 */
const aggregateSessions = (
  sessions: IComputedAggregate[]
): ICombinedComputedAggregate => {
  if (!sessions || sessions.length === 0) return {};
  return combineAggregates(sessions.reduce(sessionReducer, {}));
};

/**
 * @description combines the aggregates for each metric
 * @param computedAggregateMap {IComputedMetricAggregateMap}
 * @returns {ICombinedComputedAggregate}
 */
const combineAggregates = (
  computedAggregateMap: IComputedMetricAggregateMap
): ICombinedComputedAggregate => {
  return Object.keys(computedAggregateMap).reduce(
    (
      acc: ICombinedComputedAggregate,
      key: string
    ): ICombinedComputedAggregate => {
      acc[key] = {
        ...combineComputedAggregates(
          computedAggregateMap[key].map(
            (agg: MetricAggregate): MetricAggregate => new MetricAggregate(agg)
          )
        ).values(),
      };
      return acc;
    },
    {}
  );
};

/**
 * @description takes a list of computed aggregates and combines them
 * @param acc
 * @param curr
 */
export const sessionReducer = (
  acc: IComputedMetricAggregateMap,
  curr: IComputedAggregate
): IComputedMetricAggregateMap => {
  if (!isObject(curr?.data)) return acc;
  Object.keys(curr?.data).forEach((key: string): void => {
    const computedModel = new MetricAggregate(
      curr.data[key] as IMetricAggregateInput
    );
    acc[key] = acc[key] ? acc[key].concat(computedModel) : [computedModel];
  });
  return acc;
};
